// A Spotlight-style command palette (⌘K / Ctrl+K): one big centred search box
// that finds tunes across every source — built-in presets and native scores,
// saved charts, OpenBook, your playlists and the loaded iReal library — plus page navigation and library
// actions. Enter loads the tune and jumps to the Workbench.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { executeCommand } from '@/commands/registry';
import { ensureAppCommands, PAGES } from '@/commands/appCommands';
import { tuneSubtitle, tuneVersionLabel } from '@/data/tuneSources';
import { normalizeTuneQuery, searchTuneRows } from '@/data/tuneSearch';
import { useTuneSearchSources } from '@/data/useTuneSearchSources';
import { APP_ROUTES } from '@/navigation';
import { useDialogInteraction } from '@/components/useDialogInteraction';
import { OPEN_PALETTE_EVENT, type OpenPaletteDetail } from '@/components/paletteBus';

ensureAppCommands();

interface PaletteRow {
  key: string;
  title: string;
  composer?: string;
  context?: string;
  subtitle?: string;
  /** Small badge on the right: where this result comes from. */
  kind: string;
  kindLabel?: string;
  /** The tune carries a written melody (openbook / a saved Score with notes). */
  melody?: boolean;
  /** The tune carries sung words. */
  lyrics?: boolean;
  run: () => void | Promise<void>;
}

const MAX_RESULTS = 40;

/** Parameterless registry commands surfaced as plain palette actions. */
const ACTION_COMMANDS = [
  'toggle_theme',
  'toggle_page_view',
  'print_sheet',
  'stop_playback',
] as const;
const ACTION_LABELS: Record<(typeof ACTION_COMMANDS)[number], string> = {
  toggle_theme: 'Toggle colour theme',
  toggle_page_view: 'Toggle page view (side-by-side pages)',
  print_sheet: 'Print',
  stop_playback: 'Stop playback',
};

export function CommandPalette() {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const { rows: tuneRows, loading, error: sourceError, retry } = useTuneSearchSources(open);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setSelected(0);
    setError(null);
  }, []);
  const dialogRef = useDialogInteraction<HTMLDivElement>(open, close);

  useEffect(() => {
    const toggle = () => {
      setOpen((v) => !v);
      setQuery('');
      setSelected(0);
      setError(null);
    };
    const onOpen = (event: Event) => {
      const query = (event as CustomEvent<OpenPaletteDetail>).detail?.query;
      if (!query) return toggle();
      // Asked for a search: open ON it, never toggle the palette shut.
      setOpen(true);
      setQuery(query);
      setSelected(0);
      setError(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_PALETTE_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_PALETTE_EVENT, onOpen);
    };
  }, []);

  const rows: PaletteRow[] = useMemo(() => {
    const out: PaletteRow[] = [];

    for (const page of PAGES) {
      out.push({
        key: `nav-${page.to}`,
        title: `Go to ${page.label}`,
        kind: 'page',
        run: async () => {
          await executeCommand('go_to_page', { page: page.id }, { navigate });
          close();
        },
      });
    }

    for (const name of ACTION_COMMANDS) {
      out.push({
        key: `cmd-${name}`,
        title: ACTION_LABELS[name],
        kind: 'action',
        run: async () => {
          await executeCommand(name, {}, { navigate });
          close();
        },
      });
    }

    // Every loadable tune, via the shared source aggregation (the Tunes
    // browser reads the same list, so ⌘K and the browser always agree).
    for (const entry of tuneRows) {
      out.push({
        key: entry.key,
        title: entry.title,
        composer: entry.composer,
        context: entry.context,
        subtitle: tuneSubtitle(entry),
        kind: entry.source,
        kindLabel: tuneVersionLabel(entry.source),
        melody: entry.melody,
        lyrics: entry.lyrics,
        run: async () => {
          setBusy(true);
          setError(null);
          try {
            await entry.load();
            navigate(APP_ROUTES.workbench);
            close();
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not load that tune.');
          } finally {
            setBusy(false);
          }
        },
      });
    }

    return out;
  }, [tuneRows, navigate, close]);

  const results = useMemo(() => {
    const q = normalizeTuneQuery(query);
    if (!q) {
      // A blank palette surfaces actions and presets, not 1600+ tunes.
      return rows
        .filter(
          (r) =>
            r.kind !== 'tune' && r.kind !== 'abc' && r.kind !== 'playlist' && r.kind !== 'openbook',
        )
        .slice(0, MAX_RESULTS);
    }
    return searchTuneRows(rows, q).slice(0, MAX_RESULTS);
  }, [rows, query]);

  useEffect(() => setSelected(0), [query, open]);

  // Keep the highlighted row in view while arrowing through the list.
  useEffect(() => {
    listRef.current?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  if (!open) return null;

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[selected]) {
      e.preventDefault();
      void results[selected].run();
    }
  };

  return (
    <div className="palette-overlay" onPointerDown={close} data-testid="command-palette">
      <div
        ref={dialogRef}
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label="Search and commands"
        tabIndex={-1}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <input
          className="palette-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKey}
          placeholder={busy || loading ? 'Loading tunes…' : 'Search tunes, lessons and commands…'}
          aria-label="Command palette search"
          data-testid="palette-input"
        />
        <div className="palette-list" ref={listRef}>
          {results.map((row, i) => (
            <button
              key={row.key}
              type="button"
              className={`palette-row${i === selected ? ' selected' : ''}`}
              data-selected={i === selected}
              data-testid="palette-row"
              onPointerEnter={() => setSelected(i)}
              onClick={() => void row.run()}
            >
              <span className="palette-title">
                {row.title}
                {row.subtitle && <span className="palette-subtitle"> · {row.subtitle}</span>}
              </span>
              <span className="palette-kind">
                {row.melody && (
                  <span
                    className="palette-melody"
                    title="Has a written melody"
                    aria-label="has melody"
                  >
                    {'\u266a '}
                  </span>
                )}
                {row.lyrics && (
                  <span className="palette-lyrics" title="Has lyrics" aria-label="has lyrics">
                    {'abc '}
                  </span>
                )}
                {row.kindLabel ?? row.kind}
              </span>
            </button>
          ))}
          {results.length === 0 && <p className="palette-empty">No matches.</p>}
        </div>
        {error && (
          <p className="import-error" role="alert" style={{ margin: '6px 14px' }}>
            {error}
          </p>
        )}
        {sourceError && (
          <p className="import-error" role="status" style={{ margin: '6px 14px' }}>
            {sourceError}{' '}
            <button type="button" className="btn" onClick={retry} disabled={loading}>
              Try again
            </button>
          </p>
        )}
        <div className="palette-hint">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
}
